import React, { useEffect, useState } from "react";
import { HiOutlineBriefcase, HiOutlineLocationMarker } from "react-icons/hi";
import { MdOutlineWorkOutline, MdOutlineCalendarToday } from "react-icons/md";
import axios from "../../Utils/axiosInstance";
import CareerDetailModal from "../../Components/UserSideComponents/Home/CareerDetailModal";
import CareerApplyModal from "../../Components/UserSideComponents/Home/Components/CareerApplyModal";


/* ── Job type badge ── */
const JobTypeBadge = ({ type }) => {
  const colors = {
    "Full-time": "bg-blue-50 text-blue-600",
    "Part-time": "bg-purple-50 text-purple-600",
    Internship: "bg-amber-50 text-amber-600",
    Contract: "bg-pink-50 text-pink-600",
    Remote: "bg-teal-50 text-teal-600",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${colors[type] || "bg-gray-50 text-gray-500"}`}
    >
      {type}
    </span>
  );
};

const JOB_TYPE_FILTERS = [
  "All",
  "Full-time",
  "Part-time",
  "Internship",
  "Contract",
  "Remote",
];

const Career = () => {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [applyCareer, setApplyCareer] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    const fetchCareers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/career/get-all-active`);
        setCareers(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch career listings", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCareers();
  }, []);

  const filteredCareers = careers.filter((c) => {
    if (activeFilter !== "All" && c.jobType !== activeFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      c.title?.toLowerCase().includes(s) ||
      c.department?.toLowerCase().includes(s) ||
      c.location?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-white dark:bg-darkModeBgColor">
      {/* ── Hero Section ── */}
      <div className="bg-[#fdf8f3] dark:bg-darkModeSecondaryBgColor py-20 px-6 text-center">
        <span className="text-primaryColor text-6xl font-serif leading-none opacity-20 select-none">
          "
        </span>
        <h1 className="text-3xl md:text-5xl font-bold text-gray-800 dark:text-white -mt-6">
          Join Our <span className="text-primaryColor">Team</span>
        </h1>
        <p className="text-secondaryTextColor dark:text-darkModeSecondaryTextColor mt-3 max-w-xl mx-auto">
          Explore open positions and become part of a team that's building
          something meaningful. We'd love to hear from you.
        </p>

        {/* Search */}
        <div className="max-w-md mx-auto mt-8">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by role, department, or location..."
            className="w-full px-4 py-3 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-darkModeBgColor
              text-sm text-gray-700 dark:text-darkModeTextColor shadow-sm focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor outline-none transition"
          />
        </div>

        {/* Job type filter chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
          {JOB_TYPE_FILTERS.map((type) => (
            <button
              key={type}
              onClick={() => setActiveFilter(type)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border
                ${
                  activeFilter === type
                    ? "bg-primaryColor text-white border-primaryColor shadow-sm"
                    : "bg-white dark:bg-darkModeBgColor text-gray-500 dark:text-darkModeSecondaryTextColor border-gray-200 dark:border-gray-600 hover:border-primaryColor hover:text-primaryColor"
                }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* ── Job Listings ── */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-white dark:bg-darkModeSecondaryBgColor border border-slate-100 dark:border-0 rounded-2xl p-6 h-36"
              >
                <div className="h-4 bg-slate-200 dark:bg-gray-700 rounded w-1/4 mb-3" />
                <div className="h-5 bg-slate-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
                <div className="h-3 bg-slate-200 dark:bg-gray-700 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : filteredCareers.length === 0 ? (
          <div className="text-center py-20">
            <HiOutlineBriefcase
              size={48}
              className="mx-auto text-gray-300 dark:text-gray-600 mb-3"
            />
            <p className="font-semibold text-gray-600 dark:text-darkModeTextColor">
              No open positions found
            </p>
            <p className="text-sm text-secondaryTextColor dark:text-darkModeSecondaryTextColor mt-1">
              Try adjusting your search or filters, or check back soon for new
              openings.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold text-gray-400 mb-5 uppercase tracking-wide">
              {filteredCareers.length} open position
              {filteredCareers.length !== 1 ? "s" : ""}
            </p>

            <div className="space-y-4">
              {filteredCareers.map((career) => (
                <div
                  key={career._id}
                  className="bg-white dark:bg-darkModeSecondaryBgColor border border-slate-200 dark:border-0 rounded-2xl p-6
                    shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setSelectedCareer(career)}
                    >
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <JobTypeBadge type={career.jobType} />
                        {career.department && (
                          <span className="text-xs font-semibold text-gray-400">
                            {career.department}
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-bold text-gray-800 dark:text-white group-hover:text-primaryColor transition">
                        {career.title}
                      </h3>

                      <div className="flex items-center flex-wrap gap-4 mt-2 text-xs text-secondaryTextColor dark:text-darkModeSecondaryTextColor">
                        {career.location && (
                          <span className="inline-flex items-center gap-1">
                            <HiOutlineLocationMarker size={14} />{" "}
                            {career.location}
                          </span>
                        )}
                        {career.experienceLevel && (
                          <span className="inline-flex items-center gap-1">
                            <MdOutlineWorkOutline size={14} />{" "}
                            {career.experienceLevel}
                          </span>
                        )}
                        {career.applicationDeadline && (
                          <span className="inline-flex items-center gap-1">
                            <MdOutlineCalendarToday size={14} />
                            Apply by{" "}
                            {new Date(
                              career.applicationDeadline
                            ).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                            })}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-slate-600 dark:text-darkModeTextColor mt-3 line-clamp-2">
                        {career.description}
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => setSelectedCareer(career)}
                        className="px-5 py-2.5 rounded-full border-2 border-primaryColor text-primaryColor text-sm font-semibold hover:bg-primaryColor hover:text-white transition"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => setApplyCareer(career)}
                        className="px-5 py-2.5 rounded-full bg-primaryColor text-white text-sm font-semibold hover:opacity-90 transition"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Modals ── */}
      <CareerDetailModal
        career={selectedCareer}
        onClose={() => setSelectedCareer(null)}
        onApply={(c) => setApplyCareer(c)}
      />
      <CareerApplyModal career={applyCareer} onClose={() => setApplyCareer(null)} />
    </div>
  );
};

export default Career;